"use client";
import { Room } from "livekit-client";
import { toast } from "react-hot-toast";
import { Poll } from "@client/types/meet.types";
import { PollStatus } from "@shared/constants/constant";
import { useAppSelector } from "@client/store/store.hooks";
import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useTranslations } from "next-intl";
import { createPoll, getPolls, closePoll, votePoll } from "@client/actions/poll/poll.actions";
import { Logger } from "@shared/utils/logger";
import { buildVotersByOptionFromVotes, emptyVotersByOption, processPollVote } from "@client/utils/pollVoteUtils";

interface UsePollsOptions {
  room: Room;
  showPolls: boolean;
}

interface UsePollsReturn {
  polls: Poll[];
  unreadPolls: boolean;
  handleCreatePoll: (question: string, options: string[], isAnonymous: boolean) => void;
  handleVote: (pollId: string, optionId: number) => void;
  handleClosePoll: (pollId: string) => void;
  handlePollDataReceived: (message: Record<string, unknown>) => void;
}

const PUBLISH_OPTIONS = { reliable: true } as const;
const textEncoder = new TextEncoder();

const publishData = (room: Room, data: object) => {
  room.localParticipant.publishData(textEncoder.encode(JSON.stringify(data)), PUBLISH_OPTIONS);
};

interface DBPollVote {
  optionId: number;
  voterId: string;
  voterName?: string;
}

interface DBPoll {
  id: string;
  question: string;
  options: { id: number; text: string }[];
  votes: DBPollVote[];
  createdAt: string | Date;
  status: string | number;
  isAnonymous?: boolean;
}

const mapDbPollToLocal = (p: DBPoll): Poll => {
  const votesMap = new Map<number, number>();
  const votersMap = new Map<string, number>();
  const isAnonymous = p.isAnonymous !== false;

  if (Array.isArray(p.votes)) {
    p.votes.forEach((vote: DBPollVote) => {
      votesMap.set(vote.optionId, (votesMap.get(vote.optionId) || 0) + 1);
      votersMap.set(vote.voterId, vote.optionId);
    });
  }

  return {
    id: p.id,
    question: p.question,
    options: p.options,
    votes: votesMap,
    voters: votersMap,
    votersByOption: buildVotersByOptionFromVotes(p.votes ?? [], isAnonymous),
    isAnonymous,
    createdBy: "Host",
    createdAt: new Date(p.createdAt).getTime(),
    isActive: Number(p.status) === PollStatus.CREATED
  };
};

interface SocketPoll {
  id: string;
  question: string;
  options: { id: number; text: string }[];
  votes?: Record<string, number>;
  voters?: [string, number][];
  votersByOption?: Record<string, { voterId: string; voterName: string }[]>;
  isAnonymous?: boolean;
  createdBy: string;
  createdAt: number;
}

const mapSocketPollToLocal = (poll: SocketPoll): Poll => {
  const votesMap = new Map<number, number>();
  if (poll.votes) {
    Object.entries(poll.votes).forEach(([key, count]) => {
      votesMap.set(Number(key), Number(count));
    });
  }

  const isAnonymous = poll.isAnonymous !== false;
  const votersByOption = emptyVotersByOption();
  if (!isAnonymous && poll.votersByOption) {
    Object.entries(poll.votersByOption).forEach(([key, entries]) => {
      votersByOption.set(
        Number(key),
        entries.map((e) => ({ ...e }))
      );
    });
  }

  return {
    id: poll.id,
    question: poll.question,
    options: poll.options,
    votes: votesMap,
    voters: new Map(poll.voters || []),
    votersByOption,
    isAnonymous,
    createdBy: poll.createdBy,
    createdAt: poll.createdAt,
    isActive: true
  };
};

const serializeVotersByOption = (map: Map<number, { voterId: string; voterName: string }[]>) => {
  const record: Record<string, { voterId: string; voterName: string }[]> = {};
  for (const [optionId, entries] of map) {
    record[String(optionId)] = entries;
  }
  return record;
};

/**
 * Manages poll creation, voting, and status updates.
 * Handles both optimistic updates and server synchronization.
 */
export const usePolls = ({ room, showPolls }: UsePollsOptions): UsePollsReturn => {
  const t = useTranslations();
  const [polls, setPolls] = useState<Poll[]>([]);
  const [unreadPolls, setUnreadPolls] = useState(false);
  const roomId = useAppSelector((state) => state.connection.connectionDetails?.roomId || null);

  if (!roomId) {
    throw new Error("Room ID not found");
  }

  const roomRef = useRef(room);
  useEffect(() => {
    roomRef.current = room;
  }, [room]);

  const showPollsRef = useRef(showPolls);
  useEffect(() => {
    showPollsRef.current = showPolls;
  }, [showPolls]);

  useEffect(() => {
    if (showPolls) {
      setUnreadPolls(false);
    }
  }, [showPolls]);

  const pollsRef = useRef<Poll[]>([]);
  useEffect(() => {
    pollsRef.current = polls;
  }, [polls]);

  useEffect(() => {
    const fetchPolls = async () => {
      if (!roomId) return;

      try {
        const response = await getPolls(roomId);

        if (response.status && response.data && Array.isArray((response.data as { poll: unknown[] }).poll)) {
          const pollData = (response.data as { poll: DBPoll[] }).poll;
          const mappedPolls: Poll[] = pollData.map(mapDbPollToLocal);
          setPolls(mappedPolls);
        }
      } catch (error) {
        Logger.error("usePolls", "Error fetching polls:", error);
      }
    };

    fetchPolls();
  }, [roomId]);

  const handleCreatePoll = useCallback(
    async (question: string, options: string[], isAnonymous: boolean) => {
      const currentRoom = roomRef.current;

      const pollOptions = options.map((text, index) => ({
        id: index + 1,
        text
      }));

      const newPoll: Poll = {
        id: `poll-${Date.now()}`,
        question,
        options: pollOptions,
        votes: new Map(),
        voters: new Map(),
        votersByOption: emptyVotersByOption(),
        isAnonymous,
        createdBy: currentRoom.localParticipant.name || "Host",
        createdAt: Date.now(),
        isActive: true
      };

      setPolls((prev) => [...prev, newPoll]);

      try {
        const response = await createPoll({
          roomId,
          question,
          options: pollOptions,
          isAnonymous
        });

        if (response.status && response.data) {
          const createdPollData = response.data as { id: string };
          const realId = createdPollData.id;

          const pollForBroadcast = { ...newPoll, id: realId };
          setPolls((prev) => prev.map((p) => (p.id === newPoll.id ? pollForBroadcast : p)));

          publishData(currentRoom, {
            type: "poll-create",
            poll: {
              id: realId,
              question: pollForBroadcast.question,
              options: pollForBroadcast.options,
              isAnonymous: pollForBroadcast.isAnonymous,
              votes: Object.fromEntries(pollForBroadcast.votes),
              voters: Array.from(pollForBroadcast.voters),
              votersByOption: pollForBroadcast.isAnonymous
                ? undefined
                : serializeVotersByOption(pollForBroadcast.votersByOption),
              createdBy: pollForBroadcast.createdBy,
              createdAt: pollForBroadcast.createdAt
            }
          });
        } else {
          setPolls((prev) => prev.filter((p) => p.id !== newPoll.id));
        }
      } catch (error) {
        Logger.error("usePolls", "Error creating poll:", error);
        toast.error(t("validation.pollError"));
        setPolls((prev) => prev.filter((p) => p.id !== newPoll.id));
      }
    },
    [roomId, t]
  );

  const handleVote = useCallback(
    (pollId: string, optionId: number) => {
      const currentRoom = roomRef.current;
      const participantId = currentRoom.localParticipant.sid;
      const voterName = currentRoom.localParticipant.name || "Guest";
      const currentPolls = pollsRef.current;

      const poll = currentPolls.find((p) => p.id === pollId);

      if (!poll || !poll.isActive) return;

      if (pollId.startsWith("poll-")) {
        toast.error(t("meeting.pollCreating"));
        return;
      }

      const optionExists = poll.options.some((opt) => opt.id === optionId);
      if (!optionExists) return;

      setPolls((prev) =>
        prev.map((p) => {
          if (p.id !== pollId) return p;
          return processPollVote(p, optionId, participantId, poll.isAnonymous ? undefined : voterName);
        })
      );

      publishData(currentRoom, {
        type: "poll-vote",
        pollId,
        optionId,
        voterId: participantId,
        ...(poll.isAnonymous ? {} : { voterName })
      });

      const persistVote = async () => {
        try {
          const response = await votePoll({
            roomId,
            pollId,
            voterId: participantId,
            voterName,
            optionId
          });

          if (!response.status) {
            Logger.error("usePolls", "Failed to persist vote:", response.message);
            toast.error(t("validation.voteFailed"));
          }
        } catch (error) {
          Logger.error("usePolls", "Error persisting vote:", error);
          toast.error(t("validation.voteError"));
        }
      };

      persistVote();
    },
    [roomId, t]
  );

  const handleClosePoll = useCallback(
    async (pollId: string) => {
      setPolls((prev) => prev.map((p) => (p.id === pollId && p.isActive ? { ...p, isActive: false } : p)));

      publishData(roomRef.current, { type: "poll-close", pollId });

      try {
        const response = await closePoll({
          roomId,
          pollId,
          status: PollStatus.ENDED
        });

        if (!response.status) {
          Logger.error("usePolls", "Failed to close poll:", response.message);
          toast.error(t("validation.closePollFailed"));
        }
      } catch (error) {
        Logger.error("usePolls", "Error closing poll:", error);
        toast.error(t("validation.closePollError"));
      }
    },
    [roomId, t]
  );

  const handlePollDataReceived = useCallback((message: Record<string, unknown>) => {
    const { type, poll, pollId, optionId, voterId, voterName } = message as {
      type: string;
      poll?: SocketPoll;
      pollId?: string;
      optionId?: number;
      voterId?: string;
      voterName?: string;
    };

    switch (type) {
      case "poll-create":
        if (poll) {
          setPolls((prev) => {
            if (prev.some((p) => p.id === poll.id)) {
              return prev.map((p) => (p.id === poll.id ? mapSocketPollToLocal(poll) : p));
            }
            return [...prev, mapSocketPollToLocal(poll)];
          });
          if (!showPollsRef.current) {
            setUnreadPolls(true);
          }
        }
        break;

      case "poll-vote":
        if (pollId && optionId !== undefined && voterId) {
          setPolls((prev) =>
            prev.map((p) => {
              if (p.id !== pollId) return p;
              const name = p.isAnonymous ? undefined : voterName;
              return processPollVote(p, optionId, voterId, name);
            })
          );
        }
        break;

      case "poll-close":
        if (pollId) {
          setPolls((prev) => prev.map((p) => (p.id === pollId ? { ...p, isActive: false } : p)));
        }
        break;
    }
  }, []);

  return useMemo(
    () => ({
      polls,
      unreadPolls,
      handleCreatePoll,
      handleVote,
      handleClosePoll,
      handlePollDataReceived
    }),
    [polls, unreadPolls, handleCreatePoll, handleVote, handleClosePoll, handlePollDataReceived]
  );
};
